/**
 * Утилиты для работы с геолокацией и расстояниями
 */

// Координаты Кара-Кече (опорная точка)
export const KARA_KECHE = { lat: 41.72593448150259, lng: 74.80113720647803 };

/**
 * Парсит строку "lat,lng" в объект { lat, lng }
 * @param {string} geoStr - строка вида "41.725,74.801"
 * @returns {{ lat: number, lng: number } | null}
 */
export const parseGeo = (geoStr) => {
  if (!geoStr || typeof geoStr !== 'string') return null;
  const parts = geoStr.split(',').map(s => parseFloat(s.trim()));
  if (parts.length !== 2 || parts.some(isNaN)) return null;
  return { lat: parts[0], lng: parts[1] };
};

/**
 * Вычисляет расстояние между двумя точками по формуле Хаверсина (в метрах)
 */
export const haversineMeters = (lat1, lon1, lat2, lon2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Проверяет, находится ли точка в пределах заданного радиуса от одного из филиалов.
 * Возвращает { isInRange, distanceInfo, distances }
 *
 * @param {string} userGeoStr   "lat,lng" пользователя
 * @param {Array}  branches     массив { name, geo }
 * @param {number} radiusMeters допустимый радиус (по умолчанию 200 м)
 */
export const checkBranchesInRange = (userGeoStr, branches, radiusMeters = 200) => {
  const userGeo = parseGeo(userGeoStr);
  if (!userGeo) return { isInRange: false, distanceInfo: 'Геолокация недоступна. Разрешите доступ к геолокации.', distances: [] };

  let isInRange = false;
  const distances = [];

  if (Array.isArray(branches)) {
    branches.forEach((branch) => {
      const branchGeo = parseGeo(branch.geo);
      if (branchGeo) {
        const dist = haversineMeters(userGeo.lat, userGeo.lng, branchGeo.lat, branchGeo.lng);
        distances.push({ name: branch.name, dist });
        if (dist <= radiusMeters) {
          isInRange = true;
        }
      }
    });
  }

  let distanceInfo = '';
  if (distances.length > 0) {
    const minDist = Math.min(...distances.map(d => d.dist));
    const closest = distances.find(d => d.dist === minDist);
    distanceInfo = `${closest.name}: ${Math.round(minDist)}м`;
  } else {
    distanceInfo = 'Нет данных о филиалах';
  }

  return { isInRange, distanceInfo, distances };
};

/**
 * Проверяет удалённость точки geo_a от Кара-Кече (> 500 м → "далеко")
 */
export const isFarFromKaraKeche = (geoStr) => {
  const geo = parseGeo(geoStr);
  if (!geo) return false;
  return haversineMeters(KARA_KECHE.lat, KARA_KECHE.lng, geo.lat, geo.lng) > 500;
};

/**
 * Проверяет удалённость geo_b от ближайшего филиала (> 1000 м → "далеко")
 */
export const isFarFromBase = (geoBStr, geoBases) => {
  const geoB = parseGeo(geoBStr);
  if (!geoB || !Array.isArray(geoBases)) return false;

  let minDistance = null;
  geoBases.forEach(branch => {
    const branchGeo = parseGeo(branch.geo);
    if (branchGeo) {
      const dist = haversineMeters(branchGeo.lat, branchGeo.lng, geoB.lat, geoB.lng);
      if (minDistance === null || dist < minDistance) {
        minDistance = dist;
      }
    }
  });

  return minDistance !== null && minDistance > 1000;
};
